export const locales = ["en", "ko", "ja", "zh-TW", "fr", "es", "pt"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "en";
export const LOCALE_COOKIE = "promptopia_locale";
export const LOCALE_HEADER = "x-promptopia-locale";
export const localeNames: Record<Locale, string> = {
  en: "English", ko: "한국어", ja: "日本語", "zh-TW": "繁體中文（台灣）",
  fr: "Français", es: "Español", pt: "Português",
};

/** Normalize regional preferences to one of the translations we actually ship. */
export function normalizeLocale(value?: string | null): Locale | undefined {
  if (!value) return undefined;
  const tag = value.trim().replaceAll("_", "-").toLowerCase();
  // Only valid language tags may select a translation. Return canonical catalog
  // values below, never the untrusted query, cookie or header value itself.
  try { Intl.getCanonicalLocales(tag); } catch { return undefined; }
  if (tag === "zh" || tag.startsWith("zh-")) return "zh-TW";
  return locales.find((locale) => locale.toLowerCase() === tag || tag.startsWith(`${locale.toLowerCase()}-`));
}

export function negotiateLocale(acceptLanguage?: string | null): Locale {
  const preferences = (acceptLanguage ?? "").split(",").map((item, index) => {
    const [tag, ...parameters] = item.trim().split(";");
    const quality = parameters.find((parameter) => parameter.trim().startsWith("q="));
    const weight = quality ? Number(quality.trim().slice(2)) : 1;
    return { locale: normalizeLocale(tag), weight, index };
  }).filter((item) => item.locale && Number.isFinite(item.weight) && item.weight > 0 && item.weight <= 1)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);
  return preferences[0]?.locale ?? defaultLocale;
}

/** Use the server-rendered locale for browser-side dates and API error messages. */
export function getBrowserLocale(): Locale {
  if (typeof document === "undefined") return defaultLocale;
  const rendered = normalizeLocale(document.documentElement.lang);
  if (rendered) return rendered;
  const cookie = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(`${LOCALE_COOKIE}=`));
  return normalizeLocale(cookie?.slice(LOCALE_COOKIE.length + 1)) ?? negotiateLocale(navigator.languages?.join(",") || navigator.language);
}
